import { useAuth } from "@workos-inc/authkit-react";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEntryFiltersCache } from "@/shared/cache";
import type { FieldOptions } from "./FilterMenu";
import { normalizeOptions } from "./fields";
import type {
	EntryFilterState,
	EnumFilterField,
	NonStatusMultiSelectFilterField,
} from "./types";
import { filterStateParser, serializeEntryFilters } from "./urlState";

/**
 * Entries/Calendar filter URL format: one `filter` query param containing
 * semicolon-separated field selections, e.g.
 * `status:tracked,reviewed;timekeeper:123;application:Word,Outlook`.
 * Values are URI-component encoded. Status is always explicit and defaults to
 * tracked+reviewed. Missing non-status multi-select fields mean "all selected";
 * present empty fields mean "none selected". Timekeeper is omitted for solo
 * users; multi-timekeeper users default to the option whose sublabel matches
 * the authenticated email. This hook canonicalizes malformed/old URLs with one
 * replace navigation and all mutators replace history while preserving unrelated
 * params (date, month, sort, cols, q, etc.).
 */

const STATUS_VALUES = ["tracked", "reviewed", "exported", "deleted"] as const;
const DEFAULT_STATUS = ["tracked", "reviewed"] as const;
const MULTI_FIELDS = [
	"application",
	"matter",
	"client",
	"billableCategory",
] as const satisfies readonly NonStatusMultiSelectFilterField[];

function dedupe(values: readonly string[]): string[] {
	return Array.from(new Set(values));
}

function optionValues(
	fieldOptions: FieldOptions,
	field: EnumFilterField,
): string[] {
	return normalizeOptions(fieldOptions[field] ?? []).map(
		(option) => option.value,
	);
}

function validValues(
	fieldOptions: FieldOptions,
	field: EnumFilterField,
	values: readonly string[] | undefined,
): string[] | undefined {
	if (values === undefined) return undefined;
	const known = optionValues(fieldOptions, field);
	const unique = dedupe(values);
	if (known.length === 0) return unique;
	const allowed = new Set(known);
	return unique.filter((value) => allowed.has(value));
}

function currentUserTimekeeperId(
	fieldOptions: FieldOptions,
	email: string | null | undefined,
): string | null {
	if (!email) return null;
	const normalizedEmail = email.trim().toLowerCase();
	return (
		normalizeOptions(fieldOptions.timekeeper ?? []).find(
			(option) => option.sublabel?.trim().toLowerCase() === normalizedEmail,
		)?.value ?? null
	);
}

export function buildCanonicalEntryFilters({
	rawFilters,
	currentUserEmail,
	filterOptions,
}: {
	rawFilters: EntryFilterState | null | undefined;
	currentUserEmail?: string | null;
	filterOptions: FieldOptions;
}): EntryFilterState {
	const canonical: EntryFilterState = {
		status: rawFilters?.status
			? dedupe(rawFilters.status).filter((value) =>
					(STATUS_VALUES as readonly string[]).includes(value),
				)
			: rawFilters?.status === undefined
				? [...DEFAULT_STATUS]
				: [],
	};
	if (canonical.status.length === 0 && rawFilters?.status === undefined) {
		canonical.status = [...DEFAULT_STATUS];
	}
	if (canonical.status.length === 0 && (rawFilters?.status?.length ?? 0) > 0) {
		canonical.status = [...DEFAULT_STATUS];
	}

	for (const field of MULTI_FIELDS) {
		const values = validValues(filterOptions, field, rawFilters?.[field]);
		if (values === undefined) continue;
		const all = optionValues(filterOptions, field);
		if (all.length > 0 && values.length === all.length) continue;
		canonical[field] = values;
	}

	const timekeepers = normalizeOptions(filterOptions.timekeeper ?? []);
	if (timekeepers.length > 1) {
		const validIds = new Set(timekeepers.map((option) => option.value));
		const raw = rawFilters?.timekeeper ?? null;
		canonical.timekeeper =
			raw && validIds.has(raw)
				? raw
				: (currentUserTimekeeperId(filterOptions, currentUserEmail) ??
					timekeepers[0]?.value ??
					null);
	}

	return canonical;
}

function readRawFilters(search: string): EntryFilterState {
	const params = new URLSearchParams(search);
	return filterStateParser.parse(params.get("filter") ?? "") ?? { status: [] };
}

function withFilter(search: string, filters: EntryFilterState): string {
	const params = new URLSearchParams(search);
	params.delete("filter");
	const next = params.toString();
	const filter = serializeEntryFilters(filters);
	return `?${next ? `${next}&` : ""}filter=${filter}`;
}

export function useCanonicalEntryFilters(fieldOptions: FieldOptions) {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, isLoading: isAuthLoading } = useAuth();
	const { isLoading: isEntryFiltersLoading } = useEntryFiltersCache();
	const isReady = !isAuthLoading && !isEntryFiltersLoading;

	const rawFilters = useMemo(
		() => readRawFilters(location.search),
		[location.search],
	);
	const filters = useMemo(
		() =>
			buildCanonicalEntryFilters({
				rawFilters,
				currentUserEmail: user?.email,
				filterOptions: fieldOptions,
			}),
		[rawFilters, user?.email, fieldOptions],
	);
	const defaultFilters = useMemo(
		() =>
			buildCanonicalEntryFilters({
				rawFilters: undefined,
				currentUserEmail: user?.email,
				filterOptions: fieldOptions,
			}),
		[user?.email, fieldOptions],
	);

	useEffect(() => {
		if (!isReady) return;
		const current = new URLSearchParams(location.search).get("filter") ?? "";
		const canonical = serializeEntryFilters(filters);
		if (current === canonical) return;
		navigate(
			{
				pathname: location.pathname,
				search: withFilter(window.location.search, filters),
				hash: location.hash,
			},
			{ replace: true },
		);
	}, [
		filters,
		isReady,
		location.hash,
		location.pathname,
		location.search,
		navigate,
	]);

	const replaceFilters = useCallback(
		(next: EntryFilterState) => {
			const canonical = buildCanonicalEntryFilters({
				rawFilters: next,
				currentUserEmail: user?.email,
				filterOptions: fieldOptions,
			});
			navigate(
				{
					pathname: location.pathname,
					search: withFilter(window.location.search, canonical),
					hash: location.hash,
				},
				{ replace: true },
			);
		},
		[fieldOptions, location.hash, location.pathname, navigate, user?.email],
	);

	const setFieldValues = useCallback(
		(field: NonStatusMultiSelectFilterField, values: readonly string[]) => {
			replaceFilters({ ...filters, [field]: [...values] });
		},
		[filters, replaceFilters],
	);
	const setStatus = useCallback(
		(values: readonly string[]) =>
			replaceFilters({ ...filters, status: [...values] }),
		[filters, replaceFilters],
	);
	const setTimekeeper = useCallback(
		(value: string | null) => replaceFilters({ ...filters, timekeeper: value }),
		[filters, replaceFilters],
	);
	const selectAll = useCallback(
		(field: EnumFilterField) => {
			if (field === "status") setStatus([...STATUS_VALUES]);
			else if (field === "timekeeper") return;
			else {
				const next = { ...filters };
				delete next[field];
				replaceFilters(next);
			}
		},
		[filters, replaceFilters, setStatus],
	);
	const selectNone = useCallback(
		(field: EnumFilterField) => {
			if (field === "status") setStatus([]);
			else if (field !== "timekeeper") setFieldValues(field, []);
		},
		[setFieldValues, setStatus],
	);
	const toggleFieldValue = useCallback(
		(field: EnumFilterField, value: string) => {
			if (field === "timekeeper") return setTimekeeper(value);
			if (field === "status") {
				const has = filters.status.includes(value);
				return setStatus(
					has
						? filters.status.filter((v) => v !== value)
						: [...filters.status, value],
				);
			}
			const explicit = filters[field];
			const base = explicit ?? optionValues(fieldOptions, field);
			const has = base.includes(value);
			setFieldValues(
				field,
				has ? base.filter((v) => v !== value) : [...base, value],
			);
		},
		[filters, fieldOptions, setFieldValues, setStatus, setTimekeeper],
	);
	const reset = useCallback(
		() => replaceFilters({ status: [...DEFAULT_STATUS] }),
		[replaceFilters],
	);

	return {
		filters,
		defaultFilters,
		isReady,
		setFieldValues,
		toggleFieldValue,
		selectAll,
		selectNone,
		reset,
		setStatus,
		setTimekeeper,
	};
}
